import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { initFirebase } from '../firebase';
import { checkSubscription, getSubscriptionInfo } from '../services/subscriptionService';
import PayPalButton from '../components/PayPalButton';

/**
 * 💳 Página de Suscripción
 * 
 * Permite a los usuarios suscribirse con PayPal
 */
const Subscribe = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const uid = await initFirebase();
        setUserId(uid);
        
        if (uid) {
          const isActive = await checkSubscription(uid);
          const info = await getSubscriptionInfo(uid);
          
          setHasSubscription(isActive);
          setSubscriptionInfo(info);
        }
      } catch (error) {
        console.error('Error inicializando:', error);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const handlePaymentSuccess = (order) => {
    console.log('✅ Pago exitoso:', order);
    setPaymentSuccess(true);
    
    // Redirigir después de 3 segundos
    setTimeout(() => {
      navigate('/');
    }, 3000);
  };

  const handlePaymentError = (error) => {
    console.error('❌ Error en el pago:', error);
    alert('Hubo un error procesando tu pago. Por favor intenta nuevamente.');
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#f1f5f9'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
          <p style={{ fontSize: '1.2rem', color: '#94a3b8' }}>Cargando...</p>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#f1f5f9',
        padding: '2rem'
      }}>
        <div style={{
          textAlign: 'center',
          maxWidth: '600px',
          background: 'rgba(30, 41, 59, 0.8)',
          borderRadius: '1rem',
          border: '2px solid rgba(16, 185, 129, 0.3)',
          padding: '3rem'
        }}>
          <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>🎉</div>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            marginBottom: '1rem',
            color: '#10b981'
          }}>
            ¡Pago Exitoso!
          </h1>
          <p style={{
            fontSize: '1.2rem',
            color: '#94a3b8',
            marginBottom: '2rem'
          }}>
            Tu suscripción ha sido activada. Ahora tienes acceso completo a todo el contenido premium.
          </p>
          <p style={{ color: '#64748b' }}>
            Redirigiendo al inicio...
          </p>
        </div>
      </div>
    );
  }

  if (hasSubscription) {
    const expiryDate = subscriptionInfo?.expiryDate?.toDate();
    const daysRemaining = expiryDate 
      ? Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24))
      : 0;

    return (
      <div style={{
        minHeight: '100vh',
        background: '#0f172a',
        padding: '2rem',
        color: '#f1f5f9'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <Link
            to="/"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '0.5rem',
              color: '#3b82f6',
              textDecoration: 'none',
              marginBottom: '2rem',
              transition: 'all 0.3s ease'
            }}
          >
            ← Volver al Inicio
          </Link>

          <div style={{
            background: 'rgba(30, 41, 59, 0.8)',
            borderRadius: '1rem',
            border: '2px solid rgba(16, 185, 129, 0.3)',
            padding: '3rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>✅</div>
            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: 'bold',
              marginBottom: '1rem',
              color: '#10b981'
            }}>
              Suscripción Activa
            </h1>
            <p style={{
              fontSize: '1.2rem',
              color: '#94a3b8',
              marginBottom: '2rem'
            }}>
              Tienes acceso completo a todo el contenido premium
            </p>

            <div style={{
              background: 'rgba(15, 23, 42, 0.8)',
              borderRadius: '0.5rem',
              padding: '1.5rem',
              marginBottom: '2rem'
            }}>
              <p style={{ color: '#cbd5e1', marginBottom: '0.5rem' }}>
                Días restantes:
              </p>
              <p style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#10b981'
              }}>
                {daysRemaining} días
              </p>
              {expiryDate && (
                <p style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  Expira el: {expiryDate.toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              )}
            </div>

            <Link
              to="/"
              style={{
                display: 'inline-block',
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '0.5rem',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                transition: 'all 0.3s ease'
              }}
            >
              Explorar Contenido
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0f172a',
      padding: '2rem',
      color: '#f1f5f9'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Navigation */}
        <Link
          to="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '0.5rem',
            color: '#3b82f6',
            textDecoration: 'none',
            marginBottom: '2rem',
            transition: 'all 0.3s ease'
          }}
        >
          ← Volver al Inicio
        </Link>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>🚀</div>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: 'bold',
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Suscripción Premium
          </h1>
          <p style={{
            fontSize: '1.2rem',
            color: '#94a3b8'
          }}>
            Acceso completo a todo el contenido geopolítico
          </p>
        </div>

        {/* Pricing Card */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          borderRadius: '1rem',
          border: '2px solid rgba(245, 158, 11, 0.3)',
          padding: '3rem',
          marginBottom: '2rem'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{
              fontSize: '3.5rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem',
              color: '#f59e0b'
            }}>
              $2 USD
            </div>
            <p style={{
              fontSize: '1.2rem',
              color: '#94a3b8'
            }}>
              Acceso por 30 días
            </p>
          </div>

          {/* Features */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{
              fontSize: '1.3rem',
              marginBottom: '1rem',
              color: '#f1f5f9',
              textAlign: 'center'
            }}>
              ✨ Incluye:
            </h3>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem'
            }}>
              {[
                'Análisis geopolíticos completos',
                'IA de Gemini ilimitada',
                'Mapas interactivos',
                'Contenido exclusivo',
                'Blog premium',
                'Actualizaciones constantes'
              ].map((feature, idx) => (
                <li key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem',
                  background: 'rgba(15, 23, 42, 0.5)',
                  borderRadius: '0.5rem'
                }}>
                  <span style={{ color: '#10b981', fontSize: '1.5rem' }}>✓</span>
                  <span style={{ color: '#cbd5e1' }}>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* PayPal Button */}
          <div style={{
            background: 'rgba(15, 23, 42, 0.8)',
            borderRadius: '0.5rem',
            padding: '2rem'
          }}>
            <h3 style={{
              fontSize: '1.2rem',
              marginBottom: '1rem',
              textAlign: 'center',
              color: '#f1f5f9'
            }}>
              💳 Pagar $2 USD con PayPal
            </h3>
            {userId ? (
              <PayPalButton userId={userId} />
            ) : (
              <p style={{ textAlign: 'center', color: '#94a3b8' }}>
                Inicializando...
              </p>
            )}
            
            <div style={{
              marginTop: '1.5rem',
              padding: '1rem',
              background: 'rgba(245, 158, 11, 0.1)',
              borderRadius: '0.5rem',
              border: '1px solid rgba(245, 158, 11, 0.3)'
            }}>
              <p style={{ color: '#f59e0b', fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                ⚡ Activación Manual
              </p>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                Después de realizar el pago, envía el comprobante al correo indicado con tu ID de usuario. Tu suscripción será activada en menos de 24 horas.
              </p>
            </div>
          </div>

          {/* Security Note */}
          <div style={{
            marginTop: '2rem',
            padding: '1rem',
            background: 'rgba(59, 130, 246, 0.1)',
            borderRadius: '0.5rem',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            textAlign: 'center'
          }}>
            <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
              🔒 Pago seguro procesado por PayPal
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscribe;
